/* ==========================================================================
   fx-personalization.js — Fox-Rings ring customizer

   - character counters + optional uppercase coercion
   - live engraving preview (left / right / inside slots)
   - conditional fields driven by the customer's variant selection,
     e.g. show "Custom Text (Side 2)" only when Engraving = "2 Sides"

   Hidden fields are DISABLED, not just visually hidden. Disabled inputs are
   excluded from form submission and from HTML validation, so an unused field
   never lands on the order and a hidden required field never blocks checkout.

   No jQuery.
   ========================================================================== */
(function () {
  'use strict';

  var SLOT_EMPTY = '—';

  /* ---------------- read the customer's current option selection ---------- */
  function readSelectedOptions(scope) {
    var picked = {};
    var root = scope.closest ? scope.closest('.product, .shopify-section') : null;
    root = root || document;

    // radio / swatch style
    root.querySelectorAll('input[data-option-name]:checked').forEach(function (i) {
      picked[i.getAttribute('data-option-name').trim().toLowerCase()] = i.value.trim();
    });

    // dropdown style — name="options[Engraving]"
    root.querySelectorAll('select[name^="options["]').forEach(function (s) {
      var m = s.getAttribute('name').match(/options\[(.+)\]/);
      if (!m) return;
      var opt = s.options[s.selectedIndex];
      if (opt) picked[m[1].trim().toLowerCase()] = (opt.value || opt.textContent).trim();
    });

    return picked;
  }

  function fieldIsVisible(field, picked) {
    var optName = field.getAttribute('data-fx-when-option');
    if (!optName) return true; // no condition = always shown

    var current = picked[optName.trim().toLowerCase()];
    if (current === undefined) return true; // option not on this product

    var wanted = (field.getAttribute('data-fx-when-values') || '')
      .split(',')
      .map(function (v) { return v.trim().toLowerCase(); })
      .filter(Boolean);

    current = current.toLowerCase();
    if (wanted.indexOf(current) > -1) return true;

    // A combined option (see fx-combo-option.js) stores two choices in one
    // value: "2 custom sides / With Box". Match either half, so a field can
    // say "2 custom sides, 3 custom sides" without listing all six pairings.
    var halves = current.split('/').map(function (v) { return v.trim(); });
    if (halves.length < 2) return false;
    return halves.some(function (h) { return wanted.indexOf(h) > -1; });
  }

  /* ---------------------------------- init -------------------------------- */
  function initRoot(root) {
    if (!root) return;

    var inputs = root.querySelectorAll('.fx-pz__input');
    if (!inputs.length) return;

    /* ---- engraving preview slots ---- */
    var slots = {};
    root.querySelectorAll('[data-fx-slot-out]').forEach(function (el) {
      slots[el.getAttribute('data-fx-slot-out')] = { el: el, parts: [] };
    });

    function paint(name) {
      var s = slots[name];
      if (!s) return;
      var text = s.parts
        .filter(function (p) { return p.active && p.value; })
        .map(function (p) { return p.value; })
        .join(' · ');
      s.el.textContent = text || SLOT_EMPTY;
      s.el.classList.toggle('is-empty', !text);
    }

    if (root.dataset.fxInit !== 'true') {
      root.dataset.fxInit = 'true';

      inputs.forEach(function (input) {
        var limit = parseInt(input.getAttribute('maxlength'), 10);
        var counter = root.querySelector('[data-fx-count-for="' + input.id + '"]');
        var slotName = input.getAttribute('data-fx-slot');
        var part = null;

        if (slotName && slots[slotName]) {
          part = { value: '', active: true };
          slots[slotName].parts.push(part);
          input._fxPart = part;
          input._fxSlot = slotName;
        }

        function update() {
          if (input.dataset.fxUpper === 'true') {
            var up = input.value.toUpperCase();
            if (up !== input.value) {
              var pos = input.selectionStart;
              input.value = up;
              try { input.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
            }
          }

          var len = input.value.length;
          if (counter && !isNaN(limit) && limit > 0) {
            counter.textContent = len + ' / ' + limit;
            counter.classList.toggle('is-full', len >= limit);
            counter.classList.toggle('is-near', len < limit && len >= limit - 2);
            input.classList.toggle('is-at-max', len >= limit);
          }

          if (part) {
            part.value = input.value.trim();
            paint(slotName);
          }
        }

        input._fxUpdate = update;
        input.addEventListener('input', update);
        input.addEventListener('change', update);
        update();
      });
    }

    root._fxPaint = paint;
    root._fxSlots = slots;
    applyConditions(root);
  }

  /* --------------------- show / hide conditional fields ------------------- */
  function applyConditions(root) {
    var picked = readSelectedOptions(root);
    var anyPreviewSlotUsed = false;

    root.querySelectorAll('.fx-pz__field').forEach(function (field) {
      var show = fieldIsVisible(field, picked);

      field.hidden = !show;
      field.classList.toggle('fx-pz__field--hidden', !show);

      field.querySelectorAll('.fx-pz__input').forEach(function (input) {
        // disabled → not submitted, not validated
        input.disabled = !show;
        if (!show && input.value) {
          input.value = '';
          if (input._fxUpdate) input._fxUpdate();
        }
        if (input._fxPart) {
          input._fxPart.active = show;
          if (root._fxPaint && input._fxSlot) root._fxPaint(input._fxSlot);
        }
        if (show) anyPreviewSlotUsed = true;
      });
    });

    // hide preview columns whose fields are all switched off
    ['left', 'right', 'inside'].forEach(function (name) {
      var out = root.querySelector('[data-fx-slot-out="' + name + '"]');
      if (!out) return;
      var wrap = out.closest('.fx-pz__side') || out.closest('.fx-pz__inside');
      if (!wrap) return;
      var live = root.querySelectorAll(
        '.fx-pz__field:not([hidden]) [data-fx-slot="' + name + '"]'
      ).length;
      wrap.hidden = live === 0;
    });

    var face = root.querySelector('.fx-pz__face');
    if (face) {
      var visibleSides = face.querySelectorAll('.fx-pz__side:not([hidden])').length;
      var rule = face.querySelector('.fx-pz__rule');
      if (rule) rule.hidden = visibleSides < 2;
      face.classList.toggle('fx-pz__face--single', visibleSides < 2);
    }

    var preview = root.querySelector('[data-fx-preview]');
    if (preview) preview.hidden = !anyPreviewSlotUsed;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-fx-personalization]').forEach(initRoot);
  }

  function refreshAll() {
    document.querySelectorAll('[data-fx-personalization]').forEach(applyConditions);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // any variant option change anywhere on the page
  document.addEventListener('change', function (e) {
    if (!e.target) return;
    if (e.target.matches('input[data-option-name], select[name^="options["]')) {
      refreshAll();
    }
  });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) { initAll(e.target); });

  // Dawn swaps product markup on variant change without a section:load event
  if (window.MutationObserver) {
    var pending = null;
    var mo = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () { initAll(); refreshAll(); }, 60);
    });
    mo.observe(document.querySelector('.product') || document.body, {
      childList: true,
      subtree: true
    });
  }
})();
